import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Party } from "@/app/types/party";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Edit, Trash2, UserPlus, KeyRound } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

// Define the extended column type with meta
interface CustomColumnMeta {
  label?: string;
  placeholder?: string;
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange' | 'boolean' | 'multiSelect';
  icon?: React.ComponentType<{ className?: string }>;
  options?: Array<{ label: string; value: string | number | boolean }>;
}

type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
};

// Helper component for the table header
export const DataTableColumnHeader = ({
  column,
  title,
}: {
  column: any;
  title: string;
}) => {
  return (
    <div className="flex items-center space-x-2">
      <span>{title}</span>
      {column.getCanSort() && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => column.toggleSorting()}
        >
          <span className="sr-only">Toggle sort</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
          >
            <path d="m3 16 4 4 4-4" />
            <path d="M7 20V4" />
            <path d="m21 8-4-4-4 4" />
            <path d="M17 4v16" />
          </svg>
        </Button>
      )}
    </div>
  );
};

// Define columns for the Party data table
export const columns: ExtendedColumnDef<Party>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    cell: ({ row }) => <div className="font-medium">{row.getValue("name")}</div>,
    enableColumnFilter: true,
    meta: {
      label: "Name",
      placeholder: "Filter by name...",
      variant: "text",
    },
  },
  {
    accessorKey: "phone",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Phone" />,
    cell: ({ row }) => <div>{row.getValue("phone") || "-"}</div>,
    enableColumnFilter: true,
    meta: {
      label: "Phone",
      placeholder: "Filter by phone...",
      variant: "text",
    },
  },
  {
    accessorKey: "email",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
    cell: ({ row }) => <div>{row.getValue("email") || "-"}</div>,
    enableColumnFilter: true,
    meta: {
      label: "Email",
      placeholder: "Filter by email...",
      variant: "text",
    },
  },
  {
    accessorKey: "gstin",
    header: ({ column }) => <DataTableColumnHeader column={column} title="GSTIN" />,
    cell: ({ row }) => <div>{row.getValue("gstin") || "-"}</div>,
    enableColumnFilter: true,
    meta: {
      label: "GSTIN",
      placeholder: "Filter by GSTIN...",
      variant: "text",
    },
  },
  {
    accessorKey: "gst_type",
    header: ({ column }) => <DataTableColumnHeader column={column} title="GST Type" />,
    cell: ({ row }) => {
      const gstType = row.getValue("gst_type") as string | null;
      return <div>{gstType || "Not Specified"}</div>;
    },
    enableColumnFilter: true,
    meta: {
      label: "GST Type",
      placeholder: "Select GST type...",
      variant: "select",
      options: [
        { label: "Registered", value: "Registered" },
        { label: "Unregistered/Consumer", value: "Unregistered/Consumer" },
      ],
    },
  },
  {
    accessorKey: "party_group_id",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Group" />,
    cell: ({ row }) => {
      const party = row.original;
      return (
        <div>
          {party.party_group ? (
            <Badge variant="outline">{party.party_group.name}</Badge>
          ) : (
            <span className="text-muted-foreground">None</span>
          )}
        </div>
      );
    },
    enableColumnFilter: true,
    meta: {
      label: "Party Group",
      placeholder: "Select group...",
      variant: "select",
      options: [], // Will be populated dynamically
    },
  },
  {
    accessorKey: "site_id",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Site" />,
    cell: ({ row }) => {
      const party = row.original;
      return (
        <div>
          {party.site ? (
            <Badge variant="secondary">{party.site.name}</Badge>
          ) : (
            <span className="text-muted-foreground">None</span>
          )}
        </div>
      );
    },
    enableColumnFilter: true,
    meta: {
      label: "Site",
      placeholder: "Select site...",
      variant: "select",
      options: [], // Will be populated dynamically
    },
  },
  {
    accessorKey: "state",
    header: ({ column }) => <DataTableColumnHeader column={column} title="State" />,
    cell: ({ row }) => <div>{row.getValue("state") || "-"}</div>,
    enableColumnFilter: true,
    meta: {
      label: "State",
      placeholder: "Select state...",
      variant: "select",
      options: [
        { label: "Andhra Pradesh", value: "Andhra Pradesh" },
        { label: "Delhi", value: "Delhi" },
        { label: "Gujarat", value: "Gujarat" },
        { label: "Karnataka", value: "Karnataka" },
        { label: "Maharashtra", value: "Maharashtra" },
        { label: "Tamil Nadu", value: "Tamil Nadu" },
        { label: "Telangana", value: "Telangana" },
        { label: "Uttar Pradesh", value: "Uttar Pradesh" },
        { label: "West Bengal", value: "West Bengal" },
      ],
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const party = row.original;
      
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem 
              className="cursor-pointer"
              onClick={() => document.dispatchEvent(
                new CustomEvent("edit-party", { detail: party })
              )}
            >
              <Edit className="mr-2 h-4 w-4" />
              <span>Edit</span>
            </DropdownMenuItem>
            
            {/* Client User Actions */}
            {party.phone && (
              <>
                <DropdownMenuSeparator />
                {!party.client_user_id ? (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => document.dispatchEvent(
                      new CustomEvent("create-client-user", { detail: party })
                    )}
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    <span>Create Client User</span>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => document.dispatchEvent(
                      new CustomEvent("reset-client-pin", { detail: party })
                    )}
                  >
                    <KeyRound className="mr-2 h-4 w-4" />
                    <span>Reset Client PIN</span>
                  </DropdownMenuItem>
                )}
              </>
            )}
            
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer text-destructive"
              onClick={() => document.dispatchEvent(
                new CustomEvent("delete-party", { detail: party })
              )}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              <span>Delete</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
]; 